import { Phone, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { motion } from 'motion/react';

interface HeroProps {
  h1: string;
  subhead: string;
  heroCopy: string;
  backgroundImage: string;
  onQuoteClick: () => void;
  onGalleryClick: () => void;
}

export function Hero({
  h1,
  subhead,
  heroCopy,
  backgroundImage,
  onQuoteClick,
  onGalleryClick,
}: HeroProps) {
  return (
    <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
      {/* Background with parallax effect */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 10, repeat: Infinity, repeatType: 'reverse' }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
      </motion.div>

      {/* Content */}
      <div className="container relative z-10 mx-auto px-4 pt-24 pb-16">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-white/90 mb-4">{heroCopy}</p>
            <h1 className="text-white mb-4 text-5xl">{h1}</h1>
            <p className="text-white/80 mb-8 max-w-2xl">{subhead}</p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                onClick={onQuoteClick}
                className="bg-white text-primary hover:bg-white/90 shadow-lg hover:shadow-xl group"
              >
                Get Instant Quote
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="!bg-white/10 border-2 border-white/40 text-white hover:!bg-white/20 hover:border-white/60 backdrop-blur-md shadow-md"
              >
                <a href="tel:6468999891">
                  <Phone className="mr-2 w-4 h-4" />
                  Call 646-899-9891
                </a>
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={onGalleryClick}
                className="!bg-transparent text-white hover:!bg-white/15 border border-white/20 hover:border-white/40"
              >
                View Gallery
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/50 rounded-full" />
        </div>
      </motion.div>
    </section>
  );
}
